import { forwardRef, useEffect } from 'react';
import { View } from 'react-native';
import Animated, {
  Easing,
  useAnimatedStyle,
  useSharedValue,
  withRepeat,
  withTiming,
} from 'react-native-reanimated';
import { withUnistyles } from 'react-native-unistyles';

import { styles } from './styles';
import type { SkeletonProps } from './types';

const AnimatedView = Animated.createAnimatedComponent(withUnistyles(View));

const Skeleton = forwardRef<View, SkeletonProps>(
  ({ variant = 'text', width, height, children, style, ...props }, ref) => {
    const opacity = useSharedValue(1);

    // Apply variants to trigger conditional styles
    styles.useVariants({ variant });

    // Set up the repeating fade animation
    useEffect(() => {
      opacity.value = withRepeat(
        withTiming(0.3, {
          duration: 1000,
          easing: Easing.inOut(Easing.ease),
        }),
        -1, // Infinite repeat
        true, // Reverse animation
      );
    }, [opacity]);

    // Create animated style for the fade effect
    const animatedStyle = useAnimatedStyle(() => {
      return {
        opacity: opacity.value,
      };
    });

    // Determine default dimensions based on variant
    const getDefaultDimensions = () => {
      switch (variant) {
        case 'text':
          return { width: width || 150, height: height || 16 };
        case 'circular':
          return { width: width || 48, height: height || 48 };
        case 'rectangular':
          return { width: width || 160, height: height || 120 };
        default:
          return { width: width || 150, height: height || 16 };
      }
    };

    const defaultDimensions = getDefaultDimensions();

    return (
      <AnimatedView
        ref={ref}
        style={[
          styles.container,
          {
            width: defaultDimensions.width as number,
            height: defaultDimensions.height as number,
          },
          animatedStyle,
          style,
        ]}
        {...props}
      >
        <View style={styles.children}>{children}</View>
      </AnimatedView>
    );
  },
);

Skeleton.displayName = 'Skeleton';
export { Skeleton };
